import {
  DollarSign, Trophy, Star, ArrowRightLeft, ArrowUpRight,
  ArrowDownLeft, ShoppingCart, TrendingUp, Gift, AlertTriangle,
} from "lucide-react";
import type { AccountTransaction } from "@/hooks/useAccounts";

const TX_CONFIG: Record<string, { icon: React.ElementType; color: string; sign: string }> = {
  quiz_reward: { icon: DollarSign, color: "text-emerald-500", sign: "+" },
  completion_bonus: { icon: Trophy, color: "text-amber-500", sign: "+" },
  perfect_score_bonus: { icon: Star, color: "text-amber-400", sign: "+" },
  transfer: { icon: ArrowRightLeft, color: "text-primary", sign: "" },
  contribution: { icon: ArrowUpRight, color: "text-secondary", sign: "-" },
  withdrawal: { icon: ArrowDownLeft, color: "text-destructive", sign: "+" },
  stock_purchase: { icon: ShoppingCart, color: "text-accent", sign: "-" },
  stock_sale: { icon: TrendingUp, color: "text-emerald-500", sign: "+" },
  dividend: { icon: DollarSign, color: "text-emerald-400", sign: "+" },
  employer_match: { icon: Gift, color: "text-secondary", sign: "+" },
  emergency_expense: { icon: AlertTriangle, color: "text-destructive", sign: "-" },
  event_bonus: { icon: Gift, color: "text-primary", sign: "+" },
  achievement_bonus: { icon: Star, color: "text-amber-500", sign: "+" },
  streak_bonus: { icon: Trophy, color: "text-accent", sign: "+" },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface TransactionFeedProps {
  transactions: AccountTransaction[];
}

const TransactionFeed = ({ transactions }: TransactionFeedProps) => {
  if (transactions.length === 0) {
    return (
      <div className="glass-card p-6 text-center">
        <p className="text-muted-foreground text-sm">No transactions yet. Start learning to earn!</p>
      </div>
    );
  }

  return (
    <div className="glass-card divide-y divide-border">
      {transactions.map((tx) => {
        const config = TX_CONFIG[tx.transaction_type] || TX_CONFIG.quiz_reward;
        const Icon = config.icon;
        const isPositive = config.sign === "+";

        return (
          <div key={tx.id} className="flex items-center gap-3 px-5 py-3.5">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-muted/50`}>
              <Icon className={`w-4 h-4 ${config.color}`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{tx.description}</p>
              <p className="text-xs text-muted-foreground">{timeAgo(tx.created_at)}</p>
            </div>
            <span
              className={`font-heading font-bold text-sm whitespace-nowrap ${
                isPositive ? "text-emerald-600" : config.sign === "-" ? "text-destructive" : "text-foreground"
              }`}
            >
              {config.sign}${Math.abs(tx.amount).toLocaleString()}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default TransactionFeed;
